import { useEffect, useMemo, useState } from "react";
import { Activity, PlayCircle, RefreshCw, ChevronDown, ChevronUp, Clock, CheckCircle2, AlertCircle, History } from "lucide-react";
import { adminApi } from "../api";

function StatusPill({ status }) {
  if (status === "success") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="status-success">
      <CheckCircle2 size={12} /> OK
    </span>;
  }
  if (status === "error") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200" data-testid="status-error">
      <AlertCircle size={12} /> ERRO
    </span>;
  }
  if (status === "running") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid="status-running">
      <RefreshCw size={12} className="animate-spin" /> A CORRER
    </span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-50 text-slate-600 border border-slate-200" data-testid="status-none">
    <Clock size={12} /> S/ HIST.
  </span>;
}

function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso); const now = new Date();
  const diff = Math.round((d - now) / 1000);
  const abs = Math.abs(diff);
  const fmt = (n, unit) => `${diff < 0 ? "há" : "em"} ${n} ${unit}${n !== 1 ? "s" : ""}`;
  if (abs < 60) return diff < 0 ? "agora" : "em instantes";
  if (abs < 3600) return fmt(Math.round(abs / 60), "min");
  if (abs < 86400) return fmt(Math.round(abs / 3600), "hora");
  return fmt(Math.round(abs / 86400), "dia");
}

function JobRow({ job, onRun, onHistory, running, forceRun }) {
  const [open, setOpen] = useState(false);
  const lastStatus = job.last_result?.status;
  const status = lastStatus === "running"
    ? "running"
    : (job.last_result?.error ? "error" : (job.last_run ? "success" : "none"));
  return (
    <>
      <tr className="border-b border-[#F3F0E7] hover:bg-[#FAF8F2]" data-testid={`job-row-${job.id}`}>
        <td className="px-3 py-2.5">
          <button onClick={() => setOpen(!open)} className="text-[#4B5563] hover:text-[#0F1829]" data-testid={`job-toggle-${job.id}`}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </td>
        <td className="px-3 py-2.5">
          <div className="text-sm font-medium text-[#0F1829]">{job.name}</div>
          <div className="text-[11px] text-[#978D78] font-mono">{job.id}</div>
        </td>
        <td className="px-3 py-2.5">
          <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F2EB] text-[#4B5563] border border-[#E6E1D6]">{job.category}</span>
        </td>
        <td className="px-3 py-2.5 text-xs text-[#4B5563]">{job.cron}</td>
        <td className="px-3 py-2.5 text-xs">
          <div className="text-[#0F1829]">{formatRelative(job.next_run)}</div>
          <div className="text-[10px] text-[#978D78]">{formatDt(job.next_run)}</div>
        </td>
        <td className="px-3 py-2.5">
          <StatusPill status={status} />
          {job.last_run && <div className="text-[10px] text-[#978D78] mt-0.5">{formatRelative(job.last_run)}</div>}
        </td>
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          <button
            onClick={() => onHistory(job)}
            data-testid={`job-history-${job.id}`}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium text-[#4B5563] hover:bg-[#F5F2EB] mr-1"
            title="Ver histórico"
          >
            <History size={12} /> Histórico
          </button>
          <button
            disabled={!job.runnable || running}
            onClick={() => onRun(job.id)}
            data-testid={`job-run-${job.id}`}
            title={forceRun ? "Executar forçando (ignora atomic lock)" : "Executar (respeita atomic lock)"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white hover:bg-black disabled:bg-slate-300 disabled:cursor-not-allowed transition ${forceRun ? "bg-amber-700 hover:bg-amber-800" : "bg-[#0F1829]"}`}
          >
            {running ? <RefreshCw size={12} className="animate-spin" /> : <PlayCircle size={13} />}
            {running ? "A correr..." : (forceRun ? "Forçar" : "Executar")}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-[#FBF9F3] border-b border-[#F3F0E7]">
          <td colSpan={7} className="px-6 py-3">
            <div className="text-xs text-[#4B5563]">
              <strong>Último resultado:</strong>
              {job.last_result && Object.keys(job.last_result).length > 0 ? (
                <pre className="mt-2 p-2 bg-white border border-[#E6E1D6] rounded font-mono text-[11px] overflow-x-auto">{JSON.stringify(job.last_result, null, 2)}</pre>
              ) : (
                <span className="ml-2 text-[#978D78] italic">Sem dados de execução prévia.</span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [historyModal, setHistoryModal] = useState(null); // {job, items, loading}
  const [forceRun, setForceRun] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [j, a] = await Promise.all([adminApi.jobsList(), adminApi.jobsAudit(null, 50)]);
      setJobs(j.jobs || []);
      setAudit(a.items || []);
      setErr("");
    } catch (e) {
      setErr(e.message || "Erro a carregar jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [j, a] = await Promise.all([adminApi.jobsList(), adminApi.jobsAudit(null, 50)]);
        if (cancelled) return;
        setJobs(j.jobs || []);
        setAudit(a.items || []);
        setErr("");
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erro a carregar jobs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const seen = {};
    jobs.forEach(j => { seen[j.category] = true; });
    return Object.keys(seen).sort();
  }, [jobs]);

  const filtered = useMemo(() => {
    if (!categoryFilter) return jobs;
    return jobs.filter(j => j.category === categoryFilter);
  }, [jobs, categoryFilter]);

  const handleRun = async (jobId) => {
    setRunning(jobId);
    try {
      const r = await adminApi.jobsRun(jobId, { force: forceRun });
      const skipped = !!(r.result && (r.result.skipped || r.result.status === "skipped_this_month" || r.result.status === "skipped_this_week"));
      let toastType, toastMsg;
      if (r.background) {
        toastType = "info";
        toastMsg = `⏱ ${jobId} iniciado — continua a correr em background. Recarrega o histórico dentro de alguns minutos para veres o resultado final.`;
      } else if (r.error) {
        toastType = "error";
        toastMsg = `Erro: ${r.error}`;
      } else if (skipped) {
        toastType = "warning";
        toastMsg = `⚠ ${jobId} saltado (${r.result.reason || "atomic lock activo"}) — usa "Forçar execução" para bypass.`;
      } else {
        toastType = "success";
        toastMsg = `✓ ${jobId} executado em ${r.duration_seconds}s${forceRun ? " (forçado)" : ""}`;
      }
      setToast({ type: toastType, msg: toastMsg });
      // Refresh after small delay so audit gets the new entry
      setTimeout(loadAll, 800);
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Falha a executar job" });
    } finally {
      setRunning(null);
      setTimeout(() => setToast(null), 10000);
    }
  };

  const handleHistory = async (job) => {
    setHistoryModal({ job, items: [], loading: true });
    try {
      const r = await adminApi.jobsHistory(job.id, 100);
      setHistoryModal({ job, items: r.items || [], loading: false });
    } catch (e) {
      setHistoryModal({ job, items: [], loading: false, error: e.message });
    }
  };

  return (
    <div className="max-w-7xl" data-testid="admin-jobs-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Activity size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Cron Jobs</h1>
          <p className="text-sm text-[#4B5563]">Visibilidade e execução manual dos jobs automatizados (substituem o GCP Cloud Scheduler).</p>
        </div>
        <button onClick={loadAll} disabled={loading}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-[#E6E1D6] hover:bg-[#FAF8F2] disabled:opacity-50"
          data-testid="jobs-refresh-btn">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      {toast && (
        <div className={`mb-4 p-3 rounded border text-sm ${toast.type === "error" ? "bg-red-50 border-red-200 text-red-700" : toast.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" : toast.type === "info" ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`} data-testid="jobs-toast">
          {toast.msg}
        </div>
      )}

      {err && <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-[#978D78] uppercase tracking-[0.12em]">Categoria:</span>
        <button onClick={() => setCategoryFilter("")} className={`text-xs px-2.5 py-1 rounded-full border transition ${!categoryFilter ? "bg-[#0F1829] text-white border-[#0F1829]" : "border-[#E6E1D6] text-[#4B5563] hover:bg-[#FAF8F2]"}`} data-testid="cat-all">
          Todos ({jobs.length})
        </button>
        {categories.map(c => {
          const n = jobs.filter(j => j.category === c).length;
          return (
            <button key={c} onClick={() => setCategoryFilter(c)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${categoryFilter === c ? "bg-[#0F1829] text-white border-[#0F1829]" : "border-[#E6E1D6] text-[#4B5563] hover:bg-[#FAF8F2]"}`}
              data-testid={`cat-${c}`}>
              {c} ({n})
            </button>
          );
        })}
        <label
          className={`ml-auto inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border cursor-pointer select-none transition ${forceRun ? "bg-amber-50 border-amber-300 text-amber-800" : "border-[#E6E1D6] text-[#4B5563] hover:bg-[#FAF8F2]"}`}
          title="Se activo, remove o atomic lock antes de correr — permite re-executar mesmo que o cron já tenha corrido esta janela."
          data-testid="force-run-label"
        >
          <input
            type="checkbox"
            checked={forceRun}
            onChange={(e) => setForceRun(e.target.checked)}
            className="accent-amber-700"
            data-testid="force-run-checkbox"
          />
          <span className="font-semibold uppercase tracking-[0.08em]">Forçar execução</span>
        </label>
      </div>

      <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden mb-8">
        <table className="w-full">
          <thead className="bg-[#FAF8F2] border-b border-[#E6E1D6]">
            <tr className="text-[11px] uppercase tracking-[0.12em] text-[#978D78]">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 text-left">Job</th>
              <th className="px-3 py-2 text-left">Categoria</th>
              <th className="px-3 py-2 text-left">Schedule</th>
              <th className="px-3 py-2 text-left">Próxima execução</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Acção</th>
            </tr>
          </thead>
          <tbody>
            {loading && jobs.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#978D78] text-sm">A carregar...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#978D78] text-sm">Sem jobs nesta categoria.</td></tr>
            )}
            {filtered.map(j => <JobRow key={j.id} job={j} onRun={handleRun} onHistory={handleHistory} running={running === j.id} forceRun={forceRun} />)}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#E6E1D6] rounded-md" data-testid="jobs-audit-section">
        <div className="px-4 py-3 border-b border-[#E6E1D6] flex items-center gap-2 bg-[#FAF8F2]">
          <History size={16} className="text-[#0F1829]" />
          <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[#0F1829]">Histórico (últimas {audit.length})</h2>
        </div>
        <div className="divide-y divide-[#F3F0E7] max-h-[480px] overflow-y-auto">
          {audit.length === 0 && <div className="px-4 py-8 text-center text-[#978D78] text-sm">Sem histórico ainda.</div>}
          {audit.map(a => {
            const job = a.job || "?";
            const isManual = job.startsWith("manual_run:");
            const hasError = !!a.error;
            return (
              <div key={a._id} className="px-4 py-2.5 text-xs flex items-start gap-3 hover:bg-[#FAF8F2]" data-testid={`audit-row-${a._id}`}>
                <div className="w-32 shrink-0 text-[#978D78] font-mono">{formatDt(a.at)}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {isManual && <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">MANUAL</span>}
                    {hasError ? <AlertCircle size={12} className="text-red-600" /> : <CheckCircle2 size={12} className="text-emerald-600" />}
                    <span className="font-semibold text-[#0F1829]">{job}</span>
                  </div>
                  {Object.keys(a).filter(k => !["_id", "at", "job"].includes(k)).length > 0 && (
                    <div className="mt-1 text-[11px] text-[#4B5563] font-mono">
                      {Object.entries(a).filter(([k]) => !["_id", "at", "job"].includes(k)).map(([k, v]) => {
                        const display = v == null
                          ? "—"
                          : (typeof v === "object" ? JSON.stringify(v) : String(v));
                        return (
                          <span key={k} className="mr-3">{k}=<span className={hasError && k === "error" ? "text-red-600" : "text-[#0F1829]"}>{display.slice(0, 200)}</span></span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {historyModal && (
        <HistoryModal
          job={historyModal.job}
          items={historyModal.items}
          loading={historyModal.loading}
          error={historyModal.error}
          onClose={() => setHistoryModal(null)}
          onReload={() => handleHistory(historyModal.job)}
        />
      )}
    </div>
  );
}


function HistoryModal({ job, items, loading, error, onClose, onReload }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="job-history-modal"
      onClick={onClose}>
      <div className="bg-white border border-[#E6E1D6] rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E6E1D6] flex items-center gap-3 bg-[#FAF8F2]">
          <History size={18} className="text-[#0F1829]" />
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#0F1829]">Histórico — {job.name}</h3>
            <p className="text-[11px] font-mono text-[#978D78]">{job.id}</p>
          </div>
          <button onClick={onReload} disabled={loading}
            className="text-xs text-[#4B5563] hover:text-[#0F1829] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="history-reload">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Recarregar
          </button>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#0F1829] px-2" data-testid="history-close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="px-4 py-12 text-center text-[#978D78] text-sm">A carregar histórico...</div>
          )}
          {!loading && error && (
            <div className="px-4 py-8 text-red-700 text-sm">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-4 py-12 text-center text-[#978D78] text-sm">
              Sem execuções registadas para este job.
            </div>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#FAF8F2] border-b border-[#E6E1D6]">
                <tr className="text-[10px] uppercase tracking-[0.1em] text-[#978D78]">
                  <th className="px-3 py-2 text-left">Início</th>
                  <th className="px-3 py-2 text-left">Fim</th>
                  <th className="px-3 py-2 text-left">Duração</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Origem</th>
                  <th className="px-3 py-2 text-left">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const status = it.status || (it.error ? "error" : "ok");
                  const src = it.source || (it.job?.startsWith("manual_run:") ? "manual" : (it.job?.startsWith("scheduler:") ? "scheduled" : "internal"));
                  const detail = Object.entries(it).filter(([k]) => ![
                    "_id","at","job","job_id","started_at","finished_at",
                    "duration_seconds","status","source","triggered_by","dry_run",
                    "scheduled_run_time","error","traceback","result",
                  ].includes(k));
                  const hasResult = it.result && typeof it.result === "object" && Object.keys(it.result).length > 0;
                  return (
                    <tr key={it._id || idx} className="border-b border-[#F3F0E7] hover:bg-[#FAF8F2] align-top"
                      data-testid={`history-row-${idx}`}>
                      <td className="px-3 py-2 text-[#0F1829] whitespace-nowrap font-mono text-[11px]">
                        {formatDt(it.started_at || it.at)}
                      </td>
                      <td className="px-3 py-2 text-[#0F1829] whitespace-nowrap font-mono text-[11px]">
                        {formatDt(it.finished_at || it.at)}
                      </td>
                      <td className="px-3 py-2 text-[#4B5563] whitespace-nowrap">
                        {it.duration_seconds != null ? `${it.duration_seconds}s` : "—"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <StatusPill status={status === "success" ? "success" : (status === "ok" ? "success" : (status === "error" ? "error" : (status === "running" ? "running" : (status === "killed_by_restart" ? "error" : "none"))))} />
                        {status === "killed_by_restart" && (
                          <div className="text-[10px] text-red-600 mt-0.5">redeploy/restart</div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded ${src === "manual" ? "bg-blue-50 text-blue-700 border border-blue-200" : src === "scheduled" ? "bg-slate-50 text-slate-600 border border-slate-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                          {src === "manual" ? "MANUAL" : src === "scheduled" ? "AGENDADO" : "AUDIT"}
                        </span>
                        {it.triggered_by && (
                          <div className="text-[10px] text-[#978D78] mt-0.5">{it.triggered_by}</div>
                        )}
                        {it.dry_run && (
                          <div className="text-[10px] text-amber-700 mt-0.5">DRY_RUN</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-[#4B5563] font-mono max-w-md">
                        {it.error && (
                          <div className="text-red-700 mb-1 whitespace-pre-wrap break-words">{String(it.error).slice(0, 400)}</div>
                        )}
                        {detail.length > 0 && !it.error && (
                          <div className="text-[10px] whitespace-pre-wrap break-words mb-1">
                            {detail.slice(0, 6).map(([k, v]) => {
                              const s = v == null
                                ? "—"
                                : (typeof v === "object" ? JSON.stringify(v) : String(v));
                              return (
                                <span key={k} className="mr-3">{k}=<span className="text-[#0F1829]">{s.slice(0, 80)}</span></span>
                              );
                            })}
                          </div>
                        )}
                        {hasResult && (
                          <details className="mt-1" data-testid={`history-result-${idx}`}>
                            <summary className="cursor-pointer text-[10px] uppercase tracking-[0.1em] text-[#0F1829] hover:text-black">
                              Result payload ({Object.keys(it.result).length} chaves)
                            </summary>
                            <pre className="mt-1 p-2 bg-[#FBF9F3] border border-[#E6E1D6] rounded text-[10px] leading-tight overflow-x-auto max-w-md">{JSON.stringify(it.result, null, 2)}</pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-2.5 border-t border-[#E6E1D6] bg-[#FAF8F2] text-[11px] text-[#978D78] flex items-center justify-between">
          <span>{items.length} execuç{items.length === 1 ? "ão" : "ões"} listadas</span>
          <span className="font-mono">{job.cron}</span>
        </div>
      </div>
    </div>
  );
}
